import type { Idea, Message, Project } from "../lib/types";
import { StatusBadge } from "./StatusBadge";

interface Props {
  idea: Idea;
  messages: Message[];
  project: Project | undefined;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

export function IdeaCard({ idea, messages, project, isSelected, onSelect, onDelete }: Props) {
  const preview = getPreview(idea, messages);
  const ago = formatAgo(idea.updatedAt);

  return (
    <div
      onClick={onSelect}
      className={`rounded-xl p-4 cursor-pointer transition-all border ${
        isSelected
          ? "border-gray-800 bg-white shadow-md"
          : "border-gray-100 bg-white hover:border-gray-300 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <StatusBadge status={idea.status} />
            {project && (
              <span className="text-xs text-gray-400 font-mono truncate">{project.name}</span>
            )}
          </div>
          <h3 className="font-mono font-bold text-sm text-gray-900 truncate">{idea.title}</h3>
          {preview && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2 font-mono">{preview}</p>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-gray-300 hover:text-red-500 text-sm transition-colors shrink-0 mt-0.5"
          title="Delete idea"
        >
          ✕
        </button>
      </div>
      <div className="mt-2 text-xs text-gray-400 font-mono">{ago}</div>
    </div>
  );
}

function getPreview(idea: Idea, messages: Message[]): string {
  const lastAssistant = [...messages]
    .filter((m) => m.role === "assistant" && !m.hidden)
    .pop();
  if (lastAssistant) return lastAssistant.content;
  return idea.brainDump;
}

function formatAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
